import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmsIcon from '@mui/icons-material/Sms';
import { WechatGuideCard } from './WechatGuide';
import FloatingWidgets from './FloatingWidgets';
import apiService, { resolveFileUrl } from '../services/api';
import '../styles/theme.css';

// 科技蓝青配色方案
const COLORS = {
  primary: '#3b82f6',      // 蓝色
  secondary: '#06b6d4',    // 青色
  accent: '#06b6d4',       // 绿色
  light: '#60a5fa',        // 浅蓝
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  bgDark: '#0a0f1a',
  bgCard: 'rgba(15, 23, 42, 0.9)',
  glow: 'rgba(59, 130, 246, 0.4)',
  glowGreen: 'rgba(6, 182, 212, 0.4)',
  border: 'rgba(59, 130, 246, 0.25)',
};

// 数字格子输入组件（找回密码：补全手机号中间4位 / 6位验证码）
// 透明 input 覆盖在格子上方，点击格子直接输入，支持键盘输入与整段粘贴
const OtpBoxes = ({ value, length, onChange, onEnter, disabled, autoFocus }) => {
  const [focused, setFocused] = useState(false);
  const items = Array.from({ length }, (_, i) => value[i] || '');
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', gap: '6px' }}>
      {items.map((ch, i) => {
        const isCaret = focused && !disabled && i === Math.min(value.length, length - 1);
        return (
          <Box
            key={i}
            sx={{
              width: 40,
              height: 48,
              borderRadius: '8px',
              background: 'rgba(10, 15, 26, 0.5)',
              border: isCaret
                ? `2px solid ${COLORS.primary}`
                : ch
                  ? `1px solid ${COLORS.primary}80`
                  : '1px solid rgba(59, 130, 246, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              boxShadow: isCaret ? `0 0 10px ${COLORS.glow}` : 'none',
            }}
          >
            <Typography sx={{ color: COLORS.text, fontSize: '1.1rem', fontWeight: 700 }}>{ch}</Typography>
          </Box>
        );
      })}
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value.replace(/\D/g, '').slice(0, length)); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
        onFocus={(e) => {
          setFocused(true);
          // 防浏览器 autofill 把保存的账号/手机号填进格子：聚焦时 DOM 值与受控值不符立即还原
          if (e.target.value !== value) e.target.value = value;
        }}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        inputMode="numeric"
        autoComplete="off"
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          opacity: 0, border: 'none', outline: 'none', background: 'transparent',
          cursor: disabled ? 'default' : 'text',
        }}
      />
    </Box>
  );
};

// 星空背景
const StarField = React.memo(() => {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const shootingStarsRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    starsRef.current = [];
    for (let i = 0; i < 150; i++) {
      starsRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.8 + 0.2,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
      });
    }

    const createShootingStar = () => {
      if (shootingStarsRef.current.length < 3 && Math.random() < 0.015) {
        shootingStarsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height * 0.5,
          length: Math.random() * 80 + 40,
          speed: Math.random() * 8 + 6,
          angle: Math.PI / 4 + Math.random() * 0.3,
          opacity: 1,
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 15, 26, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      starsRef.current.forEach(star => {
        star.twinkle += star.twinkleSpeed;
        const baseOpacity = 0.3 + star.opacity * 0.7 * (0.5 + 0.5 * Math.sin(star.twinkle));

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(165, 180, 252, ${baseOpacity})`;
        ctx.fill();

        if (star.size > 1.5) {
          const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.size * 4);
          gradient.addColorStop(0, `rgba(96, 165, 250, ${baseOpacity * 0.3})`);
          gradient.addColorStop(1, 'transparent');
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 4, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();
        }
      });

      createShootingStar();

      shootingStarsRef.current = shootingStarsRef.current.filter(ss => {
        ss.x += Math.cos(ss.angle) * ss.speed;
        ss.y += Math.sin(ss.angle) * ss.speed;
        ss.opacity -= 0.012;

        if (ss.opacity <= 0) return false;

        const tailX = ss.x - Math.cos(ss.angle) * ss.length;
        const tailY = ss.y - Math.sin(ss.angle) * ss.length;

        const gradient = ctx.createLinearGradient(tailX, tailY, ss.x, ss.y);
        gradient.addColorStop(0, 'transparent');
        gradient.addColorStop(1, `rgba(59, 130, 246, ${ss.opacity})`);

        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(ss.x, ss.y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(ss.x, ss.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${ss.opacity})`;
        ctx.fill();

        return true;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resize();
      starsRef.current.forEach(star => {
        if (star.x > canvas.width) star.x = Math.random() * canvas.width;
        if (star.y > canvas.height) star.y = Math.random() * canvas.height;
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
});

// 神经网络
const NeuralNetwork = React.memo(() => {
  const canvasRef = useRef(null);
  const nodesRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const nodeCount = Math.min(25, Math.floor(window.innerWidth / 80));
    nodesRef.current = [];

    for (let i = 0; i < nodeCount; i++) {
      nodesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 3 + 2,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodesRef.current.forEach((node, i) => {
        node.pulse += 0.03;
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        const glowSize = node.size * (2 + Math.sin(node.pulse) * 0.5);
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowSize * 3);
        gradient.addColorStop(0, `rgba(59, 130, 246, ${0.4 + Math.sin(node.pulse) * 0.2})`);
        gradient.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowSize * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96, 165, 250, ${0.6 + Math.sin(node.pulse) * 0.4})`;
        ctx.fill();

        nodesRef.current.slice(i + 1).forEach(node2 => {
          const dx = node.x - node2.x;
          const dy = node.y - node2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 200) {
            const opacity = (1 - dist / 200) * 0.3;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(node2.x, node2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: 0.6,
      }}
    />
  );
});

// 背景光效
const BackgroundGlow = () => (
  <>
    <Box
      sx={{
        position: 'fixed',
        top: '-30%',
        left: '-20%',
        width: '80%',
        height: '80%',
        background: `radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 60%)`,
        filter: 'blur(80px)',
        zIndex: 0,
      }}
    />
    <Box
      sx={{
        position: 'fixed',
        bottom: '-30%',
        right: '-20%',
        width: '70%',
        height: '70%',
        background: `radial-gradient(ellipse, rgba(6, 182, 212, 0.08) 0%, transparent 60%)`,
        filter: 'blur(80px)',
        zIndex: 0,
      }}
    />
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        height: '100%',
        background: `
          radial-gradient(ellipse at 30% 20%, rgba(6, 182, 212, 0.08) 0%, transparent 50%),
          radial-gradient(ellipse at 70% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)
        `,
        zIndex: 0,
      }}
    />
  </>
);

const Login = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(mode !== 'register');
  const [mounted, setMounted] = useState(false);
  const [formStep, setFormStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);

  useEffect(() => {
    setIsLogin(mode !== 'register');
  }, [mode]);

  useEffect(() => {
    // 检测是否为移动端
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768;
      setIsMobile(mobile);
      setCanvasReady(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  
  const [focusedField, setFocusedField] = useState('');
  
  const [validationErrors, setValidationErrors] = useState({
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });
const [isInviteLocked, setIsInviteLocked] = useState(false);

// 忘记密码弹窗相关状态
const [forgotOpen, setForgotOpen] = useState(false);
const [contactQr, setContactQr] = useState('');
const [contactTitle, setContactTitle] = useState('联系客服');
const [serviceWechatId, setServiceWechatId] = useState('');
const [copiedWechat, setCopiedWechat] = useState(false);
const [forgotLoading, setForgotLoading] = useState(false);

// 短信验证码找回相关状态
const [forgotTab, setForgotTab] = useState('sms');        // 'sms'=短信找回 'service'=联系客服
const [forgotStep, setForgotStep] = useState(1);           // 1=输入账号 2=补全手机号中间4位 3=输入验证码+新密码
const [forgotAccount, setForgotAccount] = useState('');    // 用户名或手机号
const [forgotPhonePrefix, setForgotPhonePrefix] = useState('');  // 绑定手机号前3位（数据库带出）
const [forgotPhoneSuffix, setForgotPhoneSuffix] = useState('');  // 绑定手机号后4位（数据库带出）
const [forgotFullPhoneProvided, setForgotFullPhoneProvided] = useState(false); // 用户直接输入了完整手机号（免补全）
const [phoneMiddle, setPhoneMiddle] = useState('');        // 用户补全的中间4位
const [smsCode, setSmsCode] = useState('');
const [newPassword, setNewPassword] = useState('');
const [confirmNewPassword, setConfirmNewPassword] = useState('');
const [detecting, setDetecting] = useState(false);
const [smsSending, setSmsSending] = useState(false);
const [smsCountdown, setSmsCountdown] = useState(0);       // 重发倒计时（秒）
const [resetting, setResetting] = useState(false);
const [forgotError, setForgotError] = useState('');
const [forgotSuccess, setForgotSuccess] = useState('');

// 重发倒计时
useEffect(() => {
  if (smsCountdown > 0) {
    const timer = setTimeout(() => setSmsCountdown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }
}, [smsCountdown]);

// 关闭弹窗时重置短信找回流程
const closeForgotDialog = () => {
  setForgotOpen(false);
  setForgotTab('sms');
  setForgotStep(1);
  setForgotPhonePrefix('');
  setForgotPhoneSuffix('');
  setForgotFullPhoneProvided(false);
  setPhoneMiddle('');
  setSmsCode('');
  setNewPassword('');
  setConfirmNewPassword('');
  setForgotError('');
  setForgotSuccess('');
};

// 第一步：识别账号，从数据库带出绑定手机号（掩码显示）；支持传入账号（打开弹窗时自动带入登录框账号直接识别）
const handleDetectAccount = async (accountArg) => {
  setForgotError('');
  const account = String(accountArg !== undefined ? accountArg : forgotAccount).trim();
  if (!account) {
    setForgotError('请输入用户名或手机号');
    return;
  }
  setDetecting(true);
  try {
    const res = await apiService.detectForgotAccount(account);
    const data = res.data || {};
    if (data.success) {
      setForgotPhonePrefix(data.phonePrefix || '');
      setForgotPhoneSuffix(data.phoneSuffix || '');
      setForgotFullPhoneProvided(!!data.fullPhoneProvided);
      setForgotStep(2);
      // 用户直接输入完整手机号，无需补全，直接进入发送环节
      if (data.fullPhoneProvided) {
        setPhoneMiddle('');
      }
    } else {
      setForgotError(data.message || '账号识别失败');
    }
  } catch (err) {
    const msg = err.response?.data?.message || err.userMessage || '账号识别失败，请稍后重试';
    setForgotError(msg);
  } finally {
    setDetecting(false);
  }
};

// 第二步：补全中间4位后发送短信验证码（也用于第三步的重发）
const handleSendSmsCode = async () => {
  setForgotError('');
  const account = forgotAccount.trim();
  if (!account) {
    setForgotError('请输入用户名或手机号');
    return;
  }
  // 按用户名找回时必须先补全中间4位
  const needMiddle = !forgotFullPhoneProvided;
  if (needMiddle && !/^\d{4}$/.test(phoneMiddle)) {
    setForgotError('请先补全手机号中间4位');
    return;
  }
  setSmsSending(true);
  try {
    const res = await apiService.sendForgotSmsCode(account, needMiddle ? phoneMiddle : undefined);
    const data = res.data || {};
    if (data.success) {
      setForgotStep(3);
      setSmsCountdown(60);
    } else {
      setForgotError(data.message || '验证码发送失败');
    }
  } catch (err) {
    const msg = err.response?.data?.message || err.userMessage || '验证码发送失败，请稍后重试';
    setForgotError(msg);
  } finally {
    setSmsSending(false);
  }
};

// 校验验证码并重置密码
const handleResetPassword = async () => {
  setForgotError('');
  if (!smsCode.trim()) {
    setForgotError('请输入短信验证码');
    return;
  }
  if (newPassword.length < 6) {
    setForgotError('新密码长度至少6位');
    return;
  }
  if (newPassword !== confirmNewPassword) {
    setForgotError('两次输入的密码不一致');
    return;
  }
  setResetting(true);
  try {
    const res = await apiService.resetPasswordBySms({
      identifier: forgotAccount.trim(),
      code: smsCode.trim(),
      newPassword
    });
    const data = res.data || {};
    if (data.success) {
      setForgotSuccess('密码重置成功，请使用新密码登录');
      // 3秒后自动关闭弹窗并回填用户名
      setTimeout(() => {
        closeForgotDialog();
        setFormData(prev => ({ ...prev, username: forgotAccount.trim(), password: '' }));
      }, 2500);
    } else {
      setForgotError(data.message || '密码重置失败');
    }
  } catch (err) {
    const msg = err.response?.data?.message || err.userMessage || '密码重置失败，请稍后重试';
    setForgotError(msg);
  } finally {
    setResetting(false);
  }
};
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const inviteCode = searchParams.get('invite');
    if (inviteCode && !isLogin) {
      setFormData(prev => ({ ...prev, inviteCode }));
      setIsInviteLocked(true);
    } else {
      setIsInviteLocked(false);
    }
  }, [location.search, isLogin]);

  useEffect(() => {
    const savedUser = localStorage.getItem('rememberedUser');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setFormData(prev => ({
        ...prev,
        username: userData.username || '',
        password: userData.password || ''
      }));
      setRememberMe(true);
    }
  }, []);

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaCode('');
    try {
      const res = await apiService.getCaptcha();
      if (res.data && res.data.captchaId) {
        setCaptchaId(res.data.captchaId);
        setCaptchaSvg(res.data.svg);
      }
    } catch (e) {
      console.error('获取验证码失败:', e);
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  // 打开忘记密码弹窗：自动带入登录框已填写的账号并立即识别，同时加载联系二维码与客服微信号
  const openForgotDialog = async () => {
    setForgotOpen(true);

    // 自动带入登录框账号，非空则直接识别（账号不存在时留在第一步并提示，可编辑）
    const loginAccount = (formData.username || '').trim();
    if (loginAccount) {
      setForgotAccount(loginAccount);
      handleDetectAccount(loginAccount);
    }

    if (!forgotLoading && !contactQr && !serviceWechatId) {
      setForgotLoading(true);
      try {
        const [widgetRes, wechatRes] = await Promise.all([
          apiService.getFloatingWidgets(),
          apiService.getServiceWechat(),
        ]);
        // 优先取第一个启用且配置了图片的悬浮窗二维码，其次回退到公众号二维码
        const widget = (widgetRes.widgets || []).find((w) => w.enabled && w.image);
        if (widget) {
          setContactQr(resolveFileUrl(widget.image));
          setContactTitle(widget.title || '联系客服');
        } else {
          setContactQr('/wechat-qrcode.png');
        }
        if (wechatRes && wechatRes.wechatId) {
          setServiceWechatId(wechatRes.wechatId);
        }
      } catch (e) {
        console.error('加载找回密码联系信息失败:', e);
        setContactQr('/wechat-qrcode.png');
      } finally {
        setForgotLoading(false);
      }
    }
  };

  // 复制客服微信号
  const handleCopyWechatId = async () => {
    if (!serviceWechatId) return;
    try {
      await navigator.clipboard.writeText(serviceWechatId);
      setCopiedWechat(true);
      setTimeout(() => setCopiedWechat(false), 2000);
    } catch (e) {
      console.error('复制失败:', e);
    }
  };

  const validateField = (field, value) => {
    let errorMsg = '';
    switch (field) {
      case 'username':
        if (value.length < 3) errorMsg = '用户名至少需要3个字符';
        else if (value.length > 20) errorMsg = '用户名不能超过20个字符';
        else if (!/^[a-zA-Z0-9_\u4e00-\u9fa5]+$/.test(value)) errorMsg = '用户名只能包含字母、数字、下划线和中文';
        break;
      case 'phone':
        if (!/^1[3-9]\d{9}$/.test(value)) errorMsg = '请输入正确的11位手机号码';
        break;
      case 'password':
        if (value.length < 6) errorMsg = '密码至少需要6个字符';
        else if (value.length > 20) errorMsg = '密码不能超过20个字符';
        break;
      case 'confirmPassword':
        if (value !== formData.password) errorMsg = '两次输入的密码不一致';
        break;
      default: break;
    }
    return errorMsg;
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (!isLogin || field === 'username') {
      const errorMsg = validateField(field, value);
      setValidationErrors(prev => ({ ...prev, [field]: errorMsg }));
    }
  };

  const getFieldHelper = (field) => {
    if (validationErrors[field]) return validationErrors[field];
    if (field === 'phone') {
      // 注册不验证手机号，但它是找回密码的唯一凭证，常显提醒如实填写
      return focusedField === 'phone'
        ? '注册时不验证，仅用于找回密码时验证身份，请如实填写'
        : '手机号用于找回密码，请如实填写';
    }
    if (focusedField === field) {
      switch (field) {
        case 'username': return '3-20个字符，支持字母、数字、下划线和中文';
        case 'password': return '6-20个字符，建议包含字母和数字';
        case 'confirmPassword': return '请再次输入密码以确认';
        default: return '';
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      try {
        const serverStatus = await apiService.checkServerStatus();
        if (serverStatus.status === 'error') {
          throw new Error('服务器未响应');
        }
      } catch (serverCheckError) {
        throw new Error('服务器连接失败，请确保后端服务已启动');
      }
      
      if (!isLogin && !captchaCode.trim()) {
        setError('请输入图片验证码');
        setLoading(false);
        return;
      }

      let response;
      if (isLogin) {
        response = await apiService.login({
          username: formData.username,
          password: formData.password,
        });
        
        if (response.data && response.data.token) {
          localStorage.setItem('authToken', response.data.token);
          if (response.data.sessionId) localStorage.setItem('sessionId', response.data.sessionId);

          let userData = response.data.user || response.data;
          if (userData.id || userData._id) {
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            try {
              const userResponse = await apiService.getUserInfo();
              localStorage.setItem('user', JSON.stringify(userResponse.data));
            } catch (userInfoError) {
              console.error('获取用户信息失败:', userInfoError);
            }
          }
          
          if (rememberMe) {
            localStorage.setItem('rememberedUser', JSON.stringify({
              username: formData.username,
              password: formData.password
            }));
          } else {
            localStorage.removeItem('rememberedUser');
          }
          
          try {
            await apiService.refreshUserData();
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: { userData } }));
            }
          } catch (refreshError) {
            console.error('刷新用户数据失败:', refreshError);
          }
          
          const user = userData;
          if (user && user.role === 'admin') {
            navigate('/kOjtx3AXNmCsT5Wz');
          } else {
            navigate('/dashboard');
          }
        } else {
          throw new Error('服务器响应格式不正确: 缺少token');
        }
      } else {
        const usernameError = validateField('username', formData.username);
        const phoneError = validateField('phone', formData.phone);
        const passwordError = validateField('password', formData.password);
        const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);
        
        if (usernameError || phoneError || passwordError || confirmPasswordError) {
          setValidationErrors({ username: usernameError, phone: phoneError, password: passwordError, confirmPassword: confirmPasswordError });
          setError('请检查并修正表单中的错误');
          setLoading(false);
          return;
        }
        
        response = await apiService.register({
          username: formData.username, 
          phone: formData.phone, 
          password: formData.password,
          inviteCode: formData.inviteCode?.trim() || undefined,
          captchaId,
          captchaCode,
        });
        
        if (response.data && (response.data.success === true || response.status === 201)) {
          setIsLogin(true);
          setFormData({...formData, confirmPassword: ''});
          setSuccess('注册成功！请使用您的账号登录。');
        } else {
          setError(response.data?.message || '注册失败，请稍后再试');
        }
        setLoading(false);
        return;
      }
    } catch (error) {
      if (error.userMessage) setError(error.userMessage);
      else if (error.isConnectionError) setError('无法连接到服务器');
      else if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400: setError(data?.message || (isLogin ? '用户名或密码错误' : '注册信息不符合要求')); break;
          case 401: setError(data?.message || '用户名或密码错误'); break;
          case 403: setError(data?.message || '账号已被禁用'); break;
          case 404: setError('请求的API路径不存在'); break;
          case 409: setError(data?.message || '用户名或手机号已被使用'); break;
          case 429: setError('请求次数过多，请稍后再试'); break;
          case 500: setError(data?.message || '服务器内部错误'); break;
          default: setError(data?.message || (isLogin ? '登录失败' : '注册失败'));
        }
            } else {
        setError(error.message || (isLogin ? '登录失败，请稍后重试' : '注册失败，请稍后重试'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: COLORS.bgDark,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.35)',
          },
        },
      }}
    >
      <StarField />
      {!isMobile && <NeuralNetwork />}
      <BackgroundGlow />

      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          zIndex: 10,
          padding: { xs: 3, sm: 4 },
          maxWidth: '380px',
          width: '100%',
          mx: 2,
          background: COLORS.bgCard,
          backdropFilter: 'blur(32px)',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '24px',
          boxShadow: `
            0 0 100px rgba(59, 130, 246, 0.15),
            0 0 200px rgba(59, 130, 246, 0.05),
            0 32px 64px -12px rgba(0, 0, 0, 0.5)
          `,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '24px',
            padding: '1px',
            background: `linear-gradient(135deg, ${COLORS.primary}50, transparent 50%, ${COLORS.secondary}30)`,
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            MaskComposite: 'exclude',
            pointerEvents: 'none',
          },
          // 卡片边框呼吸光效
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: -2,
            borderRadius: '26px',
            background: `linear-gradient(135deg, ${COLORS.primary}20, ${COLORS.secondary}20)`,
            zIndex: -1,
            filter: 'blur(20px)',
            animation: 'borderGlow 3s ease-in-out infinite',
            '@keyframes borderGlow': {
              '0%, 100%': { opacity: 0.3 },
              '50%': { opacity: 0.6 },
            },
          },
        }}
      >
        {/* Logo 区域 */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* 悬浮 Logo */}
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              mb: 2,
            }}
          >
            {/* 外圈光环 */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: { xs: 100, sm: 110 },
                height: { xs: 100, sm: 110 },
                transform: 'translate(-50%, -50%)',
                border: `1px dashed ${COLORS.primary}30`,
                borderRadius: '50%',
                animation: 'spin 20s linear infinite',
                '@keyframes spin': {
                  from: { transform: 'translate(-50%, -50%) rotate(0deg)' },
                  to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
                },
              }}
            />

            {/* Logo 本体 */}
            <Box
              sx={{
                width: { xs: 80, sm: 88 },
                height: { xs: 80, sm: 88 },
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.accent} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                position: 'relative',
                animation: 'float 3s ease-in-out infinite, glowPulse 2s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-8px)' },
                },
                '@keyframes glowPulse': {
                  '0%, 100%': { boxShadow: `0 0 30px ${COLORS.glow}` },
                  '50%': { boxShadow: `0 0 50px ${COLORS.glow}, 0 0 80px ${COLORS.glow}` },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '24px',
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`,
                  zIndex: -1,
                  filter: 'blur(12px)',
                  opacity: 0.6,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, transparent 50%)`,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '2rem', sm: '2.2rem' },
                  fontWeight: 800,
                  color: '#fff',
                  letterSpacing: '-2px',
                  fontFamily: "'Inter', system-ui, sans-serif",
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                }}
              >
                KM
              </Typography>
            </Box>

            {/* 环绕电子 */}
            {[0, 120, 240].map((angle, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 6,
                  height: 6,
                  background: COLORS.secondary,
                  borderRadius: '50%',
                  boxShadow: `0 0 12px ${COLORS.secondary}`,
                  animation: 'orbit 3s linear infinite',
                  animationDelay: `${i * 1}s`,
                  marginLeft: -3,
                  marginTop: -3,
                  '@keyframes orbit': {
                    '0%': { transform: `rotate(${angle}deg) translateX(50px) scale(0.5)`, opacity: 0.3 },
                    '50%': { opacity: 1, transform: `rotate(${angle + 180}deg) translateX(50px) scale(1)` },
                    '100%': { transform: `rotate(${angle + 360}deg) translateX(50px) scale(0.5)`, opacity: 0.3 },
                  },
                }}
              />
            ))}
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.7rem' },
              fontWeight: 800,
              background: `linear-gradient(135deg, ${COLORS.text} 0%, ${COLORS.light} 50%, ${COLORS.accent} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5,
              letterSpacing: '-0.5px',
            }}
          >
              KM-AI智能写作
            </Typography>
            
          <Typography
            variant="body2"
            sx={{
              color: COLORS.textMuted,
              fontSize: '0.85rem',
            }}
          >
              {isLogin ? '欢迎回来，请登录您的账户' : '创建新账户，开启AI写作之旅'}
            </Typography>
        </Box>
          
          {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#fca5a5',
              borderRadius: '12px',
              animation: 'shake 0.5s ease-in-out',
              '@keyframes shake': {
                '0%, 100%': { transform: 'translateX(0)' },
                '25%': { transform: 'translateX(-5px)' },
                '75%': { transform: 'translateX(5px)' },
              },
              '& .MuiAlert-icon': { color: '#f87171' },
              '& .MuiAlert-message': { fontSize: '0.85rem' },
            }}
          >
              {error}
            </Alert>
          )}
          
          {success && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              background: `${COLORS.glow}`,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text,
              borderRadius: '12px',
              '& .MuiAlert-icon': { color: COLORS.primary },
              '& .MuiAlert-message': { fontSize: '0.85rem' },
            }}
          >
              {success}
            </Alert>
          )}
          
        <Box component="form" onSubmit={handleSubmit}>
          {/* 用户名 - 左对齐 */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: focusedField === 'username' ? COLORS.light : COLORS.textMuted,
                mb: 0.5,
                display: 'block',
                fontWeight: 500,
                fontSize: '0.8rem',
                textAlign: 'left',
                transition: 'color 0.2s',
              }}
            >
              用户名
            </Typography>
            <TextField
              fullWidth
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              onFocus={() => setFocusedField('username')}
              onBlur={() => setFocusedField('')}
              disabled={loading}
              error={!!validationErrors.username}
              helperText={getFieldHelper('username')}
              placeholder="请输入用户名"
              InputProps={{
                sx: {
            background: `rgba(59, 130, 246, 0.03)`,
            borderRadius: '12px',
            height: 48,
            textAlign: 'left',
            '& fieldset': {
              borderColor: focusedField === 'username' ? `${COLORS.primary}60` : `rgba(59, 130, 246, 0.1)`,
                    transition: 'all 0.2s',
                  },
                  '&:hover fieldset': { borderColor: `${COLORS.primary}50` },
                  '&.Mui-focused fieldset': {
                    borderColor: COLORS.primary,
                    borderWidth: '2px',
                    boxShadow: `0 0 0 3px ${COLORS.glow}`,
                  },
                  '& .MuiInputBase-input': {
                    color: COLORS.text,
                    fontSize: '0.9rem',
                    textAlign: 'left',
                    '&::placeholder': { color: 'rgba(148, 163, 184, 0.35)', opacity: 1 },
                  },
                },
              }}
              FormHelperTextProps={{
                sx: {
                  color: validationErrors.username ? '#f87171' : COLORS.textMuted,
                  fontSize: '0.7rem',
                  textAlign: 'left',
                },
              }}
            />
          </Box>

          {/* 手机号 */}
            {!isLogin && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: focusedField === 'phone' ? COLORS.light : COLORS.textMuted,
                  mb: 0.5,
                  display: 'block',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textAlign: 'left',
                }}
              >
                手机号
              </Typography>
              <TextField
                fullWidth
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField('')}
                disabled={loading}
                error={!!validationErrors.phone}
                helperText={getFieldHelper('phone')}
                placeholder="请输入11位手机号"
                inputProps={{ maxLength: 11 }}
                InputProps={{
                  sx: {
                    background: `rgba(59, 130, 246, 0.03)`,
                    borderRadius: '12px',
                    height: 48,
                    '& fieldset': {
                      borderColor: focusedField === 'phone' ? `${COLORS.primary}60` : `rgba(59, 130, 246, 0.1)`,
                    },
                    '&:hover fieldset': { borderColor: `${COLORS.primary}50` },
                    '&.Mui-focused fieldset': {
                      borderColor: COLORS.primary,
                      borderWidth: '2px',
                      boxShadow: `0 0 0 3px ${COLORS.glow}`,
                    },
                    '& .MuiInputBase-input': {
                      color: COLORS.text,
                      fontSize: '0.9rem',
                      textAlign: 'left',
                      '&::placeholder': { color: 'rgba(148, 163, 184, 0.35)', opacity: 1 },
                    },
                  },
                }}
                FormHelperTextProps={{
                  sx: {
                    color: validationErrors.phone ? '#f87171' : COLORS.textMuted,
                    fontSize: '0.7rem',
                    textAlign: 'left',
                  },
                }}
              />
            </Box>
          )}

          {/* 密码 */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: focusedField === 'password' ? COLORS.light : COLORS.textMuted,
                mb: 0.5,
                display: 'block',
                fontWeight: 500,
                fontSize: '0.8rem',
                textAlign: 'left',
              }}
            >
              密码
            </Typography>
            <TextField
              fullWidth
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField('')}
              disabled={loading}
              error={!!validationErrors.password}
              helperText={getFieldHelper('password')}
              placeholder="请输入密码"
              InputProps={{
                sx: {
                  background: `rgba(59, 130, 246, 0.03)`,
                  borderRadius: '12px',
                  height: 48,
                  '& fieldset': {
                    borderColor: focusedField === 'password' ? `${COLORS.primary}60` : `rgba(59, 130, 246, 0.1)`,
                  },
                  '&:hover fieldset': { borderColor: `${COLORS.primary}50` },
                  '&.Mui-focused fieldset': {
                    borderColor: COLORS.primary,
                    borderWidth: '2px',
                    boxShadow: `0 0 0 3px ${COLORS.glow}`,
                  },
                  '& .MuiInputBase-input': {
                    color: COLORS.text,
                    fontSize: '0.9rem',
                    textAlign: 'left',
                    '&::placeholder': { color: 'rgba(148, 163, 184, 0.35)', opacity: 1 },
                  },
                },
              }}
              FormHelperTextProps={{
                sx: {
                  color: validationErrors.password ? '#f87171' : COLORS.textMuted,
                  fontSize: '0.7rem',
                  textAlign: 'left',
                },
              }}
            />
          </Box>

          {/* 确认密码 */}
            {!isLogin && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: focusedField === 'confirmPassword' ? COLORS.light : COLORS.textMuted,
                  mb: 0.5,
                  display: 'block',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textAlign: 'left',
                }}
              >
                确认密码
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField('')}
                disabled={loading}
                error={!!validationErrors.confirmPassword}
                helperText={getFieldHelper('confirmPassword')}
                placeholder="请再次输入密码"
                InputProps={{
                  sx: {
                    background: `rgba(59, 130, 246, 0.03)`,
                    borderRadius: '12px',
                    height: 48,
                    '& fieldset': {
                      borderColor: focusedField === 'confirmPassword' ? `${COLORS.primary}60` : `rgba(59, 130, 246, 0.1)`,
                    },
                    '&:hover fieldset': { borderColor: `${COLORS.primary}50` },
                    '&.Mui-focused fieldset': {
                      borderColor: COLORS.primary,
                      borderWidth: '2px',
                      boxShadow: `0 0 0 3px ${COLORS.glow}`,
                    },
                    '& .MuiInputBase-input': {
                      color: COLORS.text,
                      fontSize: '0.9rem',
                      textAlign: 'left',
                      '&::placeholder': { color: 'rgba(148, 163, 184, 0.35)', opacity: 1 },
                    },
                  },
                }}
                FormHelperTextProps={{
                  sx: {
                    color: validationErrors.confirmPassword ? '#f87171' : COLORS.textMuted,
                    fontSize: '0.7rem',
                    textAlign: 'left',
                  },
                }}
              />
            </Box>
          )}

          {/* 邀请码 */}
            {!isLogin && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: COLORS.textMuted,
                  mb: 0.5,
                  display: 'block',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textAlign: 'left',
                }}
              >
                邀请码（选填）
              </Typography>
              <TextField
                fullWidth
                value={formData.inviteCode}
                onChange={(e) => handleInputChange('inviteCode', e.target.value)}
                disabled={loading || isInviteLocked}
                placeholder="请输入邀请码"
                InputProps={{
                  readOnly: isInviteLocked,
                  sx: {
                    background: `rgba(59, 130, 246, 0.03)`,
                    borderRadius: '12px',
                    height: 48,
                    '& fieldset': { borderColor: `rgba(59, 130, 246, 0.1)` },
                    '& .MuiInputBase-input': {
                      color: COLORS.text,
                      fontSize: '0.9rem',
                      textAlign: 'left',
                      '&::placeholder': { color: 'rgba(148, 163, 184, 0.35)', opacity: 1 },
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* 验证码 */}
            {!isLogin && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: COLORS.textMuted,
                  mb: 0.5,
                  display: 'block',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textAlign: 'left',
                }}
              >
                图片验证码
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {captchaLoading ? (
                  <Box
                    sx={{
                      width: 110,
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(59, 130, 246, 0.05)',
                      border: '1px solid rgba(148, 163, 184, 0.1)',
                      borderRadius: '12px',
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: COLORS.primary }} />
                  </Box>
                ) : (
                  <Box
                    onClick={loadCaptcha}
                    title="点击刷新验证码"
                    sx={{
                      cursor: 'pointer',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      width: 110,
                      height: 48,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(59, 130, 246, 0.05)',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: COLORS.primary,
                        boxShadow: `0 0 16px ${COLORS.glow}`,
                      },
                    }}
                  >
                    {captchaSvg ? (
                      <Box
                        sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        dangerouslySetInnerHTML={{ __html: captchaSvg }}
                      />
                    ) : (
                      <Typography sx={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>点击加载</Typography>
                    )}
                  </Box>
                )}
                <TextField
                  fullWidth
                  value={captchaCode}
                  onChange={(e) => setCaptchaCode(e.target.value)}
                  disabled={loading}
                  placeholder="请输入验证码"
                  inputProps={{ maxLength: 6 }}
                  InputProps={{
                    sx: {
                      background: `rgba(59, 130, 246, 0.03)`,
                      borderRadius: '12px',
                      height: 48,
                      '& fieldset': { borderColor: `rgba(59, 130, 246, 0.1)` },
                      '& .MuiInputBase-input': {
                        color: COLORS.text,
                        fontSize: '0.9rem',
                        '&::placeholder': { color: 'rgba(148, 163, 184, 0.35)', opacity: 1 },
                      },
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(148, 163, 184, 0.5)', fontSize: '0.7rem', mt: 0.5, display: 'block', textAlign: 'left' }}>
                点击图片可刷新，不区分大小写
              </Typography>
            </Box>
            )}

          {/* 记住我 + 忘记密码 */}
            {isLogin && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, ml: -0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                    color: 'rgba(148, 163, 184, 0.3)',
                    '&.Mui-checked': { color: COLORS.primary },
                    }}
                    disabled={loading}
                  />
                }
              label={<Typography sx={{ color: COLORS.textMuted, fontSize: '0.85rem', textAlign: 'left' }}>记住账号密码</Typography>}
              />
              <Typography
                onClick={openForgotDialog}
                sx={{
                  color: COLORS.light,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'color 0.2s',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -2,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s',
                  },
                  '&:hover': {
                    color: COLORS.text,
                    '&::after': { transform: 'scaleX(1)' },
                  },
                }}
              >
                忘记密码？
              </Typography>
              </Box>
          )}

          {/* 提交按钮 */}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
            sx={{
              height: 52,
              borderRadius: '12px',
              background: loading
                ? 'rgba(59, 130, 246, 0.15)'
                : `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 600,
              letterSpacing: '1px',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: `0 8px 32px ${COLORS.glow}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primary} 100%)`,
                boxShadow: `0 12px 40px ${COLORS.glow}`,
                transform: 'translateY(-2px)',
                filter: 'brightness(1.1)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)',
                transition: 'left 0.6s',
              },
              '&:hover::before': { left: '100%' },
              '&.Mui-disabled': {
                background: 'rgba(99, 102, 241, 0.1)',
                color: 'rgba(255, 255, 255, 0.4)',
              },
            }}
              startIcon={loading ? null : (isLogin ? <LoginIcon /> : <PersonAddIcon />)}
            >
              {loading ? (
              <CircularProgress size={24} sx={{ color: COLORS.light }} />
              ) : (
                isLogin ? '登 录' : '注 册'
              )}
            </Button>
            
          {/* 切换登录/注册 */}
          <Grid container justifyContent="center" sx={{ mt: 2.5 }}>
                <Button
                  onClick={() => {
                    const nextIsLogin = !isLogin;
                    setIsLogin(nextIsLogin);
                    setError('');
                    setSuccess('');
                setValidationErrors({ username: '', phone: '', password: '', confirmPassword: '', inviteCode: '' });
                    setFocusedField('');
                    if (nextIsLogin) {
                      navigate('/login', { replace: true });
                    } else {
                      const nextSearch = location.search || '';
                      navigate(`/register${nextSearch}`, { replace: true });
                    }
                  }}
                  sx={{ 
                color: COLORS.light,
                fontWeight: 500,
                fontSize: '0.85rem',
                py: 0.5,
                px: 2,
                borderRadius: '8px',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: '1px',
                  background: COLORS.primary,
                  transition: 'width 0.3s',
                },
                    '&:hover': {
                  background: 'transparent',
                  color: COLORS.text,
                  '&::after': { width: '80%' },
                },
                  }}
                  disabled={loading}
                >
                  {isLogin ? '没有账号？立即注册' : '已有账号？立即登录'}
                </Button>
            </Grid>
          </Box>

          {/* 公众号引导 */}
          <WechatGuideCard compact />
        </Paper>

      {/* 网站主页右下角浮动弹窗（管理员后台可配置标题与二维码） */}
      <FloatingWidgets />

      {/* 友情链接（姊妹站点） */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 44,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <Typography
          component="a"
          href="https://cy9527.cn/"
          target="_blank"
          rel="noopener"
          sx={{
            color: 'rgba(148, 163, 184, 0.45)',
            fontSize: '0.72rem',
            textDecoration: 'none',
            transition: 'color 0.2s',
            '&:hover': { color: COLORS.light },
          }}
        >
          友情链接：CY-AI音乐助手 — AI音乐一键生成，支持发布汽水音乐
        </Typography>
      </Box>

      {/* 找回密码弹窗 */}
      <Dialog
        open={forgotOpen}
        onClose={closeForgotDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: COLORS.bgCard,
            backdropFilter: 'blur(32px)',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '20px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(6, 182, 212, 0.12) 100%)`,
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.8,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {forgotTab === 'sms' ? (
                <SmsIcon sx={{ color: '#fff', fontSize: 20 }} />
              ) : (
                <SupportAgentIcon sx={{ color: '#fff', fontSize: 21 }} />
              )}
            </Box>
            <Box>
              <Typography sx={{ color: COLORS.text, fontSize: '1.05rem', fontWeight: 700 }}>
                找回密码
              </Typography>
              <Typography sx={{ color: COLORS.textMuted, fontSize: '0.72rem' }}>
                {forgotTab === 'sms' ? '通过短信验证码快速找回' : '通过人工核实快速重置'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeForgotDialog} sx={{ color: COLORS.textMuted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 2.5 }}>
          {/* 方式切换 Tab */}
          <Box
            sx={{
              display: 'flex',
              p: 0.5,
              mb: 2.5,
              background: 'rgba(10, 15, 26, 0.6)',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              gap: 0.5,
            }}
          >
            {[
              { key: 'sms', label: '短信找回' },
              { key: 'service', label: '联系客服' },
            ].map((tab) => (
              <Box
                key={tab.key}
                onClick={() => { setForgotTab(tab.key); setForgotError(''); }}
                sx={{
                  flex: 1,
                  py: 0.75,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  background: forgotTab === tab.key
                    ? `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
                    : 'transparent',
                  color: forgotTab === tab.key ? '#fff' : COLORS.textMuted,
                  fontWeight: forgotTab === tab.key ? 700 : 400,
                  boxShadow: forgotTab === tab.key ? `0 4px 16px ${COLORS.glow}` : 'none',
                  '&:hover': forgotTab === tab.key
                    ? {}
                    : { background: 'rgba(59, 130, 246, 0.1)', color: COLORS.text },
                }}
              >
                {tab.label}
              </Box>
            ))}
          </Box>

          {/* ========== 短信验证码找回 ========== */}
          {forgotTab === 'sms' && (
            <Box>
              {/* 重置成功状态 */}
              {forgotSuccess ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 4,
                    animation: 'fadeInUp 0.4s ease',
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(12px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'rgba(16, 185, 129, 0.12)',
                      border: '2px solid rgba(16, 185, 129, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <CheckIcon sx={{ color: '#10b981', fontSize: 30 }} />
                  </Box>
                  <Typography sx={{ color: '#10b981', fontSize: '1rem', fontWeight: 700, mb: 0.5 }}>
                    密码重置成功
                  </Typography>
                  <Typography sx={{ color: COLORS.textMuted, fontSize: '0.82rem' }}>
                    即将跳转登录，请使用新密码登录
                  </Typography>
                </Box>
              ) : forgotStep === 1 ? (
                /* 第一步：输入账号（手机号由系统从数据库带出） */
                <Box>
                  <Typography sx={{ color: COLORS.textMuted, fontSize: '0.82rem', mb: 1.5 }}>
                    输入注册时的用户名或手机号，系统将识别该账号绑定的手机号
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="用户名 / 手机号"
                    value={forgotAccount}
                    onChange={(e) => { setForgotAccount(e.target.value); setForgotError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDetectAccount(); }}
                    disabled={detecting}
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(10, 15, 26, 0.5)',
                        borderRadius: '10px',
                        '& fieldset': { borderColor: COLORS.border },
                        '&:hover fieldset': { borderColor: COLORS.primary },
                        '&.Mui-focused fieldset': { borderColor: COLORS.primary },
                      },
                      '& .MuiInputBase-input': { color: COLORS.text, fontSize: '0.9rem' },
                    }}
                  />
                  {forgotError && (
                    <Typography sx={{ color: '#f87171', fontSize: '0.78rem', mt: 1 }}>
                      {forgotError}
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    disabled={detecting || !forgotAccount.trim()}
                    onClick={handleDetectAccount}
                    sx={{
                      mt: 2,
                      height: 44,
                      borderRadius: '10px',
                      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      transition: 'all 0.3s',
                      boxShadow: `0 6px 20px ${COLORS.glow}`,
                      '&:hover': { filter: 'brightness(1.1)', boxShadow: `0 8px 28px ${COLORS.glow}` },
                      '&.Mui-disabled': { background: 'rgba(59, 130, 246, 0.15)', color: 'rgba(255,255,255,0.4)' },
                    }}
                  >
                    {detecting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '下一步'}
                  </Button>
                </Box>
              ) : forgotStep === 2 ? (
                /* 第二步：补全手机号中间4位确认身份 */
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                      px: 1.5,
                      py: 1,
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                    }}
                  >
                    <SmsIcon sx={{ color: COLORS.primary, fontSize: 16 }} />
                    <Typography sx={{ color: 'rgba(241, 245, 249, 0.9)', fontSize: '0.8rem' }}>
                      已识别账号 <Box component="span" sx={{ color: COLORS.light, fontWeight: 700 }}>{forgotAccount.trim()}</Box> 绑定的手机号
                    </Typography>
                  </Box>

                  <Typography sx={{ color: COLORS.textMuted, fontSize: '0.82rem', mb: 1.5, textAlign: 'center' }}>
                    {forgotFullPhoneProvided
                      ? '确认手机号无误后，验证码将发送至该号码'
                      : '为确认身份，请补全手机号中间4位，验证码将发送至该号码'}
                  </Typography>

                  {/* 前3位 + 中间4位格子输入 + 后4位；直接输入了完整手机号时显示完整号码 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <Typography sx={{ color: COLORS.text, fontSize: '1.15rem', fontWeight: 700, letterSpacing: '2px' }}>
                      {forgotPhonePrefix}
                    </Typography>
                    {forgotFullPhoneProvided ? (
                      <Typography sx={{ color: COLORS.light, fontSize: '1.15rem', fontWeight: 700, letterSpacing: '2px' }}>
                        {forgotAccount.trim()}
                      </Typography>
                    ) : (
                      <OtpBoxes
                        value={phoneMiddle}
                        length={4}
                        onChange={(v) => { setPhoneMiddle(v); setForgotError(''); }}
                        onEnter={handleSendSmsCode}
                        disabled={smsSending}
                        autoFocus
                      />
                    )}
                    <Typography sx={{ color: COLORS.text, fontSize: '1.15rem', fontWeight: 700, letterSpacing: '2px' }}>
                      {forgotPhoneSuffix}
                    </Typography>
                  </Box>
                  {forgotError && (
                    <Typography sx={{ color: '#f87171', fontSize: '0.78rem', mt: 1 }}>
                      {forgotError}
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    disabled={smsSending || (!forgotFullPhoneProvided && phoneMiddle.length !== 4)}
                    onClick={handleSendSmsCode}
                    sx={{
                      mt: 1.5,
                      height: 44,
                      borderRadius: '10px',
                      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      transition: 'all 0.3s',
                      boxShadow: `0 6px 20px ${COLORS.glow}`,
                      '&:hover': { filter: 'brightness(1.1)', boxShadow: `0 8px 28px ${COLORS.glow}` },
                      '&.Mui-disabled': { background: 'rgba(59, 130, 246, 0.15)', color: 'rgba(255,255,255,0.4)' },
                    }}
                  >
                    {smsSending ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '发送短信验证码'}
                  </Button>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                    <Typography
                      onClick={() => { setForgotStep(1); setPhoneMiddle(''); setForgotError(''); }}
                      sx={{ color: COLORS.textMuted, fontSize: '0.78rem', cursor: 'pointer', '&:hover': { color: COLORS.text } }}
                    >
                      返回上一步
                    </Typography>
                  </Box>
                </Box>
              ) : (
                /* 第三步：输入验证码与新密码 */
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                      px: 1.5,
                      py: 1,
                      background: 'rgba(16, 185, 129, 0.08)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '8px',
                    }}
                  >
                    <SmsIcon sx={{ color: '#10b981', fontSize: 16 }} />
                    <Typography sx={{ color: 'rgba(241, 245, 249, 0.9)', fontSize: '0.8rem' }}>
                      验证码已发送至 <Box component="span" sx={{ color: '#10b981', fontWeight: 700 }}>{forgotPhonePrefix}****{forgotPhoneSuffix}</Box>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                    <OtpBoxes
                      value={smsCode}
                      length={6}
                      onChange={(v) => { setSmsCode(v); setForgotError(''); }}
                      onEnter={handleResetPassword}
                      disabled={resetting}
                      autoFocus
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    placeholder="新密码（至少6位）"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setForgotError(''); }}
                    disabled={resetting}
                    sx={{
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(10, 15, 26, 0.5)',
                        borderRadius: '10px',
                        '& fieldset': { borderColor: COLORS.border },
                        '&:hover fieldset': { borderColor: COLORS.primary },
                        '&.Mui-focused fieldset': { borderColor: COLORS.primary },
                      },
                      '& .MuiInputBase-input': { color: COLORS.text, fontSize: '0.9rem' },
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    placeholder="确认新密码"
                    value={confirmNewPassword}
                    onChange={(e) => { setConfirmNewPassword(e.target.value); setForgotError(''); }}
                    disabled={resetting}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword(); }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(10, 15, 26, 0.5)',
                        borderRadius: '10px',
                        '& fieldset': { borderColor: COLORS.border },
                        '&:hover fieldset': { borderColor: COLORS.primary },
                        '&.Mui-focused fieldset': { borderColor: COLORS.primary },
                      },
                      '& .MuiInputBase-input': { color: COLORS.text, fontSize: '0.9rem' },
                    }}
                  />
                  {forgotError && (
                    <Typography sx={{ color: '#f87171', fontSize: '0.78rem', mt: 1 }}>
                      {forgotError}
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    disabled={resetting || !smsCode.trim() || !newPassword || !confirmNewPassword}
                    onClick={handleResetPassword}
                    sx={{
                      mt: 2,
                      height: 44,
                      borderRadius: '10px',
                      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      transition: 'all 0.3s',
                      boxShadow: `0 6px 20px ${COLORS.glow}`,
                      '&:hover': { filter: 'brightness(1.1)', boxShadow: `0 8px 28px ${COLORS.glow}` },
                      '&.Mui-disabled': { background: 'rgba(59, 130, 246, 0.15)', color: 'rgba(255,255,255,0.4)' },
                    }}
                  >
                    {resetting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '重置密码'}
                  </Button>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                    <Typography
                      onClick={() => { setForgotStep(2); setSmsCode(''); setForgotError(''); }}
                      sx={{ color: COLORS.textMuted, fontSize: '0.78rem', cursor: 'pointer', '&:hover': { color: COLORS.text } }}
                    >
                      返回上一步
                    </Typography>
                    <Typography
                      onClick={() => { if (smsCountdown <= 0 && !smsSending && !resetting) handleSendSmsCode(); }}
                      sx={{
                        color: (smsCountdown > 0 || smsSending) ? COLORS.textMuted : COLORS.light,
                        fontSize: '0.78rem',
                        cursor: (smsCountdown > 0 || smsSending) ? 'default' : 'pointer',
                        '&:hover': (smsCountdown > 0 || smsSending) ? {} : { color: COLORS.text },
                      }}
                    >
                      {smsSending ? '发送中…' : smsCountdown > 0 ? `${smsCountdown}s 后可重新发送` : '重新发送验证码'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ========== 联系客服 ========== */}
          {forgotTab === 'service' && (
            <Box>
          {/* 操作步骤 */}
          <Box sx={{ mb: 2 }}>
            {[
              '扫码添加客服，或复制微信号添加',
              '提供您的注册用户名或手机号',
              '核实身份后，客服将为您重置密码',
            ].map((step, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 1 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: '2px',
                  }}
                >
                  {i + 1}
                </Box>
                <Typography sx={{ color: 'rgba(241, 245, 249, 0.85)', fontSize: '0.83rem' }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* 联系二维码 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: 168,
                height: 168,
                background: '#fff',
                borderRadius: '14px',
                p: '10px',
                boxShadow: `0 0 30px ${COLORS.glow}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {forgotLoading ? (
                <CircularProgress size={30} sx={{ color: COLORS.primary }} />
              ) : contactQr ? (
                <img
                  src={contactQr}
                  alt={contactTitle}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                  二维码未配置
                </Typography>
              )}
            </Box>
            <Typography sx={{ color: COLORS.textMuted, fontSize: '0.75rem', mt: 1.2, textAlign: 'center' }}>
              扫一扫二维码，联系{contactTitle}
            </Typography>

            {/* 客服微信号（可选） */}
            {serviceWechatId && (
              <Tooltip title={copiedWechat ? '已复制' : '点击复制微信号'} arrow>
                <Box
                  onClick={handleCopyWechatId}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    mt: 1.2,
                    px: 1.8,
                    py: 0.7,
                    background: 'rgba(59, 130, 246, 0.08)',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.15)',
                      borderColor: COLORS.primary,
                    },
                  }}
                >
                  <Typography sx={{ color: COLORS.text, fontSize: '0.82rem', fontWeight: 600 }}>
                    {serviceWechatId}
                  </Typography>
                  {copiedWechat ? (
                    <CheckIcon sx={{ color: '#10b981', fontSize: 15 }} />
                  ) : (
                    <ContentCopyIcon sx={{ color: COLORS.textMuted, fontSize: 15 }} />
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Login; 
